import Pagination from "../../entities/Pagination";
import {action, computed, makeObservable, observable, runInAction} from "mobx";
import CommonStore from "../../stores/CommonStore";
import {ArticleDeliveryService} from "../../service/articleDelivery/ArticleDeliveryService";
import ArticleDeliveryOrderResponse from "../../model/articleDelivery/ArticleDeliveryOrderResponse";
import DeliveryPerson from "../../entities/DeliveryPerson";
import Geo from "../../entities/Geo";
import ClientContact from "../../entities/ClientContact";
import Agency from "../../entities/Agency";
import ProductOrderStatus from "../../entities/productOrder/ProductOrderStatus";
import Article from "../../entities/Article";
import DeliveryStep from "../../entities/DeliveryStep";
import { Filter } from "../../util/Filter";
import CustomRange from "../../ui_components/DateTimeRageSelect/CustomRange";

class ArticleDeliveryStore {

    public articleRemittances: ArticleDeliveryOrderResponse[];
    public selectedArticleRemittanceIds: string[];
    public selectedArticleRemittanceId: string;
    public pagination: Pagination;

    public filter?: string;
    public statusFilter?: string[];
    public deliveryPersonFilter?: DeliveryPerson;
    public articleFilter?: Article;
    public fromFilter: number | undefined;
    public toFilter: number | undefined;
    public agencyFilter?: Agency;
    public geoFilter?: Geo;
    public range: CustomRange;

    public commonStore: CommonStore;

    constructor(commonStore: CommonStore, range: CustomRange) {
        this.pagination = new Pagination(1, 30, 0, 0);
        this.articleRemittances = [];
        this.selectedArticleRemittanceIds = [];
        this.selectedArticleRemittanceId = '';
        this.commonStore = commonStore;
        this.range = range;
        this.fromFilter = Filter.get("from", range.from?.valueOf() || undefined);
        this.toFilter = Filter.get("to", range?.to?.valueOf() || undefined);
        this.filter = Filter.get("filter", "");
        this.articleFilter = Filter.get("article", undefined);
        this.statusFilter = Filter.get("status", []);
        this.agencyFilter = Filter.get("agency", undefined);
        this.deliveryPersonFilter = Filter.get("deliveryPerson", undefined);
        this.geoFilter = Filter.get("geo", undefined);

        makeObservable(this, {
            articleRemittances: observable,
            selectedArticleRemittanceIds: observable,
            pagination: observable,
            agencyFilter: observable,
            isEmpty: computed
        });

    }

    get isEmpty(): boolean {
        return this.articleRemittances.length === 0;
    }

    get haveNotSelected(): boolean {
        return this.selectedArticleRemittanceIds.length === 0;
    }

    get disableUpdateBulkStatus(): boolean {
        if (this.selectedArticleRemittanceIds.length === 0) {
            return true;
        }
        const tempOrders = this.articleRemittances.filter(t => this.selectedArticleRemittanceIds.indexOf(t.id) >= 0);
        return tempOrders.findIndex(t => t.status === ProductOrderStatus.DELIVERED ||  t.status === ProductOrderStatus.CANCELED) >= 0;
    }

    get disableUpdateBulkDeliveryPerson(): boolean {
        if (this.selectedArticleRemittanceIds.length === 0) {
            return true;
        }
        const tempOrders = this.articleRemittances.filter(t => this.selectedArticleRemittanceIds.indexOf(t.id) >= 0);
        return tempOrders.findIndex(t => t.status === ProductOrderStatus.DELIVERED || t.status === ProductOrderStatus.CANCELED || t.status === ProductOrderStatus.PROCESSING) >= 0;
    }

    public setFilter(filter: string) {
        this.filter = filter;
        Filter.save("filter", filter);
    }

    public setStatusToFilter(selectedStatus?: string[]) {
        this.statusFilter = selectedStatus;
        Filter.save("status", selectedStatus);
    }

    public setDeliveryPersonToFilter(deliveryPersonFilter?: DeliveryPerson) {
        this.deliveryPersonFilter = deliveryPersonFilter;
        Filter.save("deliveryPerson", deliveryPersonFilter);
    }

    public setFromFilter(from?: number) {
        this.fromFilter = from;
        Filter.save("from", from);
    }

    public setToFilter(to?: number) {
        this.toFilter = to;
        Filter.save("to", to);
    }

    public setAgencyFilter(agencyFilter?: Agency) {
        this.agencyFilter = agencyFilter;
        Filter.save("agency", agencyFilter);
    }

    public setArticleFilter(articleFilter?: Article) {
        this.articleFilter = articleFilter;
        Filter.save("article", articleFilter);
    }

    public setGeoFilter(geoFilter?: Geo) {
        this.geoFilter = geoFilter;
        Filter.save("geo", geoFilter);
    }

    @action
    public async search() {
        this.selectedArticleRemittanceIds = [];
        await this.getElements(1);
    }

    @action
    public async clearFilter() {
        this.selectedArticleRemittanceIds = [];
        this.deliveryPersonFilter = undefined;
        Filter.save("deliveryPerson", this.deliveryPersonFilter);
        this.articleFilter = undefined;
        Filter.save("article", this.articleFilter);
        this.statusFilter = undefined;
        Filter.save("status", this.statusFilter);
        this.geoFilter = undefined;
        Filter.save("geo", this.geoFilter);
        this.fromFilter = this.range.from?.valueOf() || undefined;
        Filter.save("from", this.fromFilter);
        this.toFilter = this.range.to?.valueOf() || undefined;
        Filter.save("to", this.toFilter);
        this.agencyFilter = undefined;
        Filter.save("agency", this.agencyFilter);
        this.filter = undefined;
        Filter.save("filter", this.filter);
        await this.getElements(1);
    }


    @action
    public async goToPage(page: number = this.pagination.currentPage, pageSize: number = this.pagination.pageSize): Promise<void> {
        await this.getElements(page, pageSize);
    }

    @action
    public async getElements(page: number = this.pagination.currentPage, pageSize: number = this.pagination.pageSize): Promise<void> {
        this.commonStore.setLoading();
        const response = await ArticleDeliveryService.get(
            page, pageSize, this.fromFilter, this.toFilter, this.filter, this.deliveryPersonFilter?.id, this.articleFilter?.id,
            this.statusFilter, this.geoFilter?.id, this.agencyFilter?.id);
        if (response.success) {
            runInAction(() => {
                this.articleRemittances = [];
                setTimeout(() => {
                    this.articleRemittances = response.data?.data || [];    
                }, 0);
                this.pagination = response.data?.pagination || this.pagination;
            })
        }
        this.commonStore.processErrors(response);
        this.commonStore.removeLoading();
    }

    @action
    public async updateArticleRemittanceContact(contact: ClientContact) {
        this.commonStore.setLoading();
        // const response = await ArticleDeliveryService.updateClientPerson(this.selectedArticleRemittanceId, contact.id);
        // if (response.success) {
        //     runInAction(() => {
        //         const tempArticleRemittance = this.articleRemittances.find(t => t.id === this.selectedArticleRemittanceId);
        //         const tempArticleRemittanceIndex = this.articleRemittances.findIndex(t => t.id === this.selectedArticleRemittanceId);
        //         if (tempArticleRemittance != null) {
        //             this.articleRemittances[tempArticleRemittanceIndex] = {
        //                 ...tempArticleRemittance,
        //                 contactId: contact.id,
        //                 contactName: contact.name
        //             };
        //         }
        //     })
        // }
        // this.commonStore.processErrors(response);
        this.commonStore.removeLoading();
    }

    @action
    public async setArticleDeliveryFlow(articleDeliveryIds: string[], deliveryFlow: DeliveryStep[], resetFlow: boolean, warehouseStep?: any | null) {
        this.commonStore.setLoading();
        const response = await ArticleDeliveryService.setDeliveryFlow(articleDeliveryIds, deliveryFlow, resetFlow, warehouseStep);
        if (response.success) {
            runInAction(() => {
                this.selectedArticleRemittanceIds = [];
            })
        }
        this.commonStore.processErrors(response);
        this.commonStore.removeLoading();
    }
    
}

export default ArticleDeliveryStore;